import { Dispatch } from "redux";
import { loadAndTrack, isRequestActive } from "redux-request-loading";

import * as moment from "moment";

import { Payload } from "@neworbit/redux-helpers";
import { CalendarView, Appointment } from "@teamprevent/common/calendar";
import axios from "axios";
import { ThunkDispatch } from "redux-thunk";

import { ApplicationState, EntityCalendars } from "./models";
import { calendarApi } from "./calendarApi";
import * as actionTypes from "./actionTypes";

type DeleteAppointmentSuccess = ({ type: typeof actionTypes.DELETE_APPOINTMENT_SUCCESS }) & Payload<{
    appointmentId: string;
    clinicId?: string;
}>;

export type CalendarAction =
    ({ type: typeof actionTypes.LOAD_APPOINTMENTS_SUCCESS } & Payload<EntityCalendars>) |
    ({ type: typeof actionTypes.LOAD_APPOINTMENT_SUCCESS } & Payload<Appointment>) |
    DeleteAppointmentSuccess;

export type CalendarFilterAction =
    ({ type: typeof actionTypes.UPDATE_CALENDAR_SELECTED_USER_IDS } & Payload<string[]>) |
    ({ type: typeof actionTypes.UPDATE_CALENDAR_SELECTED_ROOM_IDS } & Payload<string[]>);

export const loadAppointmentsSuccess = (payload: EntityCalendars): CalendarAction => ({
    payload,
    type: actionTypes.LOAD_APPOINTMENTS_SUCCESS
});

export const loadEntityAppointments = (userIds: string[], roomIds: string[], startDate: moment.Moment, view: CalendarView) => {
    const endDate = startDate.clone().add(1, view);
    return async (dispatch: Dispatch) => {
        if (userIds.length + roomIds.length === 0) {
            return;
        }
        try {
            const result = await loadAndTrack(
                dispatch,
                actionTypes.LOAD_APPOINTMENTS,
                calendarApi.getInDateRange(userIds, roomIds, startDate, endDate));
            const payload = result.reduce((calendars, calendarDetails) => {
                const { entityId, ...details } = calendarDetails;
                return { ...calendars, [entityId]: details };
            }, {});
            dispatch(loadAppointmentsSuccess(payload));
        } catch (ex) {
            if (!(ex instanceof axios.Cancel)) { // eslint-disable-line import/no-named-as-default-member
                throw ex;
            }
        }
    };
};

export const loadAppointmentDetail = ({ appointmentId }: { appointmentId: string }) => {
    return async (dispatch: Dispatch, getState: () => ApplicationState) => {
        const isLoading = isRequestActive(getState(), actionTypes.LOAD_APPOINTMENT);
        if (isLoading) {
            return null;
        }
        const result = await loadAndTrack(
            dispatch,
            actionTypes.LOAD_APPOINTMENT,
            calendarApi.get(appointmentId));
        dispatch(loadAppointmentSuccess(result));

        return result;
    };
};

export const deleteAppointment = (appointmentId: string, clinicId?: string) => {
    return async (dispatch: ThunkDispatch<ApplicationState, undefined, any>) => {
        await loadAndTrack(
            dispatch,
            actionTypes.DELETE_APPOINTMENT,
            calendarApi.deleteAppointment(appointmentId, clinicId));
        dispatch(deleteAppointmentSuccess(appointmentId, clinicId));
    };
};

export const loadAppointmentSuccess = (payload: Appointment): CalendarAction => ({
    payload,
    type: actionTypes.LOAD_APPOINTMENT_SUCCESS
});

export const updateCalendarSelectedUserIds = (payload: string[]): CalendarFilterAction => ({
    payload,
    type: actionTypes.UPDATE_CALENDAR_SELECTED_USER_IDS
});

export const updateCalendarSelectedRoomIds = (payload: string[]): CalendarFilterAction => ({
    payload,
    type: actionTypes.UPDATE_CALENDAR_SELECTED_ROOM_IDS
});

export const deleteAppointmentSuccess = (appointmentId: string, clinicId?: string): DeleteAppointmentSuccess => ({
    payload: {
        appointmentId,
        clinicId
    },
    type: actionTypes.DELETE_APPOINTMENT_SUCCESS
});
